import type { ValidationIssueFamily } from './validationIssueFamilies';
import {
  normalizeValidationId,
  resolveCanonicalValidationTitle,
  resolveValidationIdCriticalityRank,
} from '../validationConstants';

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function buildPreviewList(items: string[], limit = 3): string {
  if (items.length === 0) {
    return 'None listed';
  }

  const preview = items.slice(0, limit).join(', ');
  return items.length > limit ? `${preview} +${items.length - limit} more` : preview;
}

const CATEGORY_LABELS: Record<
  ValidationIssueFamily['category'],
  { short: string; long: string }
> = {
  mapping: { short: 'Mapping', long: 'Mapping validation' },
  'required-export': { short: 'Required export data', long: 'Required export data' },
  connectors: { short: 'Connectors', long: 'Connector validation' },
  properties: { short: 'Properties', long: 'Property validation' },
  components: { short: 'Objects / Components', long: 'Objects / Components' },
};

export function formatCategory(
  category: ValidationIssueFamily['category'],
  variant: 'short' | 'long' = 'long'
): string {
  return CATEGORY_LABELS[category]?.[variant] ?? CATEGORY_LABELS.components[variant];
}

export function resolvePrimaryValidationId(family: ValidationIssueFamily): number | null {
  if (family.validationIds.length === 0) {
    return null;
  }

  return family.validationIds.reduce((bestId, candidateId) =>
    resolveValidationIdCriticalityRank(candidateId) < resolveValidationIdCriticalityRank(bestId)
      ? candidateId
      : bestId
  );
}

export function resolvePrimaryValidationTitle(family: ValidationIssueFamily): string {
  const primaryValidationId = resolvePrimaryValidationId(family);
  if (primaryValidationId != null) {
    const matchingRawIssue = family.rawIssues.find(
      (issue) => normalizeValidationId(issue.ValidationId) === normalizeValidationId(primaryValidationId)
    );
    if (matchingRawIssue?.ValidationTitle) {
      return resolveCanonicalValidationTitle(primaryValidationId, matchingRawIssue.ValidationTitle);
    }

    const matchingCheckTitle = family.checkTitles[0];
    return resolveCanonicalValidationTitle(primaryValidationId, matchingCheckTitle || family.title);
  }

  return family.checkTitles[0] || family.title;
}

export function resolveFamilyCountLabel(
  family: ValidationIssueFamily,
  variant: 'card' | 'detail' = 'detail'
): string {
  switch (family.countStrategy) {
    case 'fields':
      return pluralize(family.affectedCount, 'field');
    case 'elements':
      return pluralize(family.affectedCount, 'item');
    case 'issues':
      return pluralize(family.issueCount, 'issue');
    default:
      if (variant === 'card' && family.impactedElementCount > 0) {
        return pluralize(family.impactedElementCount, 'item');
      }

      return variant === 'card'
        ? pluralize(family.affectedCount, 'affected item')
        : pluralize(family.affectedCount, 'item');
  }
}
